import { Outlet, Link, useLocation, useNavigate } from 'react-router-dom';
import RouteErrorBoundary from '@/components/RouteErrorBoundary';
import { useAuth } from '@/context/AuthContext';
import { useDeveloperMode } from '@/hooks/useDeveloperMode';
import { useLanguage } from '@/context/LanguageContext';
import LanguageSelector from '@/components/LanguageSelector';
import OfflineIndicator from '@/components/OfflineIndicator';
import GlobalSearch from '@/components/GlobalSearch';
import { 
  LayoutDashboard, 
  Users, 
  FileText, 
  Receipt, 
  Settings, 
  Plus,
  LogOut,
  Menu,
  X,
  Building2,
  Calendar,
  ClipboardCheck,
  HelpCircle,
  TrendingUp
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useState, useEffect } from 'react';
import { cn } from '@/lib/utils';

export const AppLayout = () => {
  const { organization, user, logout, hasPermission } = useAuth();
  const developerMode = useDeveloperMode();
  const { t, setOrgDefault } = useLanguage();
  const location = useLocation();
  const navigate = useNavigate();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  
  // Set org default language when organization loads
  useEffect(() => {
    if (organization?.preferences?.default_language) {
      setOrgDefault(organization.preferences.default_language);
    }
  }, [organization?.preferences?.default_language, setOrgDefault]);
  
  const isAdmin = user?.role === 'owner' || user?.role === 'admin';
  
  // Check if user can access settings
  const canAccessSettings = hasPermission('access_settings') || isAdmin;
  
  // Get terminology preference
  const terminology = organization?.preferences?.terminology_mode || 'customers';
  
  const navItems = [
    { path: '/dashboard', label: t('dashboard'), icon: LayoutDashboard },
    { path: '/clients', label: t(terminology), icon: Users },
    { path: '/site-visits', label: t('site_visits_nav'), icon: ClipboardCheck },
    { path: '/estimates', label: t('estimates'), icon: FileText },
    { path: '/jobs', label: t('jobs'), icon: Calendar },
    { path: '/invoices', label: t('invoices'), icon: Receipt },
  ];
  
  // Leads nav — only show if feature flag is enabled
  const leadsEnabled = organization?.feature_flags?.leads;
  if (leadsEnabled) {
    navItems.push({ path: '/leads', label: 'Leads', icon: TrendingUp });
  }

  // Settings is now permission-gated
  const adminNavItems = canAccessSettings ? [
    { path: '/settings', label: t('settings'), icon: Settings },
  ] : [];
  
  // Help is available to everyone
  const helpItem = { path: '/help', label: t('help'), icon: HelpCircle };
  
  const allNavItems = isAdmin 
    ? [...navItems, ...adminNavItems, helpItem] 
    : [...navItems, helpItem];
  
  const primaryColor = organization?.brand_primary_color || '#1e3a8a';
  const accentColor = organization?.brand_accent_color || '#fbbf24';

  return (
    <div className="min-h-screen bg-background">
      {/* Desktop/Tablet Sidebar - Hidden on mobile, shown on md+ */}
      <aside className="hidden md:flex flex-col w-64 lg:w-64 md:w-56 border-r border-gray-200 bg-white h-screen fixed left-0 top-0 z-40 overflow-hidden">
        {/* Logo/Company Header */}
        <div className="p-4 lg:p-6 border-b border-gray-100 flex-shrink-0">
          <Link to="/dashboard" className="flex items-center gap-2 lg:gap-3">
            {organization?.logo_url ? (
              <img 
                src={organization.logo_url} 
                alt={organization.company_name}
                className="h-8 lg:h-10 w-auto max-w-[100px] lg:max-w-[120px] object-contain"
              />
            ) : (
              <div 
                className="w-8 h-8 lg:w-10 lg:h-10 rounded-xl flex items-center justify-center text-white font-bold text-base lg:text-lg flex-shrink-0"
                style={{ backgroundColor: primaryColor }}
              >
                {organization?.company_name?.charAt(0) || 'B'}
              </div>
            )}
            <span className="font-heading font-semibold text-base lg:text-lg text-foreground truncate">
              {organization?.company_name || 'ContractorOS'}
            </span>
          </Link>
          
          {/* Global Search */}
          <div className="mt-4">
            <GlobalSearch />
          </div>
        </div>
        
        {/* Navigation - Scrollable area */}
        <nav className="flex-1 p-3 lg:p-4 space-y-1 overflow-y-auto">
          {allNavItems.map((item) => {
            const Icon = item.icon;
            const isActive = location.pathname === item.path || 
                           (item.path !== '/dashboard' && location.pathname.startsWith(item.path));
            
            return (
              <Link
                key={item.path}
                to={item.path}
                className={cn(
                  "flex items-center gap-2 lg:gap-3 px-3 lg:px-4 py-2.5 lg:py-3 rounded-xl text-base lg:text-lg font-medium transition-all",
                  isActive 
                    ? "bg-primary text-white shadow-sm" 
                    : "text-muted-foreground hover:bg-accent hover:text-foreground"
                )}
                style={isActive ? { backgroundColor: primaryColor } : {}}
              >
                <Icon className="w-5 h-5 flex-shrink-0" />
                <span className="truncate">{item.label}</span>
              </Link>
            );
          })}
        </nav>
        
        {/* Bottom Section - Fixed at bottom */}
        <div className="flex-shrink-0 border-t border-gray-100">
          {/* New Site Visit Button */}
          <div className="p-3 lg:p-4">
            <Button 
              onClick={() => navigate('/site-visits?action=new')}
              className="w-full h-12 lg:h-14 text-base lg:text-lg font-bold rounded-xl shadow-sm transition-transform active:scale-95"
              style={{ backgroundColor: accentColor, color: '#000' }}
              data-testid="new-site-visit-sidebar-btn"
            >
              <Plus className="w-5 h-5 mr-2 flex-shrink-0" />
              <span className="truncate">{t('new_site_visit')}</span>
            </Button>
            <Button 
              onClick={() => navigate('/estimates/new')}
              variant="outline"
              className="w-full h-10 lg:h-12 mt-2 text-sm lg:text-base font-medium rounded-xl"
              data-testid="quick-estimate-sidebar-btn"
            >
              <FileText className="w-4 h-4 mr-2 flex-shrink-0" />
              <span className="truncate">{t('quick_estimate')}</span>
            </Button>
          </div>
          
          {/* User Info */}
          <div className="p-3 lg:p-4 border-t border-gray-100">
            <div className="flex items-center gap-2 lg:gap-3 mb-2 lg:mb-3">
              <div 
                className="w-8 h-8 lg:w-10 lg:h-10 rounded-full flex items-center justify-center text-white font-medium flex-shrink-0"
                style={{ backgroundColor: primaryColor }}
              >
                {user?.name?.charAt(0) || 'U'}
              </div>
              <div className="flex-1 min-w-0">
                <p className="font-medium text-foreground truncate text-sm lg:text-base">{user?.name}</p>
                <p className="text-xs lg:text-sm text-muted-foreground capitalize">{user?.role}</p>
              </div>
            </div>
            
            {/* Language Selector */}
            <div className="mb-2">
              <LanguageSelector variant="outline" showLabel={true} />
            </div>
            
            {/* Offline Indicator */}
            <div className="mb-2">
              <OfflineIndicator showLabel={true} className="w-full justify-start" />
            </div>
            
            <Button 
              variant="ghost" 
              onClick={logout}
              className="w-full justify-start text-muted-foreground hover:text-destructive h-10 lg:h-auto"
              data-testid="logout-btn"
            >
              <LogOut className="w-4 h-4 mr-2 flex-shrink-0" />
              <span className="truncate">{t('logout')}</span>
            </Button>
          </div>
        </div>
      </aside>
      
      {/* Mobile Header */}
      <header className="md:hidden fixed top-0 left-0 right-0 z-50 bg-white border-b border-gray-200 px-4 py-4">
        <div className="flex items-center justify-between">
          <Link to="/dashboard" className="flex items-center gap-3 min-w-0 flex-1 mr-2">
            {organization?.logo_url ? (
              <img 
                src={organization.logo_url} 
                alt={organization.company_name}
                className="h-10 w-auto max-w-[48px] object-contain flex-shrink-0"
              />
            ) : (
              <div 
                className="w-10 h-10 rounded-xl flex items-center justify-center text-white font-bold text-lg flex-shrink-0"
                style={{ backgroundColor: primaryColor }}
              >
                {organization?.company_name?.charAt(0) || 'B'}
              </div>
            )}
            <span className="font-heading font-semibold text-foreground text-base leading-tight line-clamp-2">
              {organization?.company_name || 'ContractorOS'}
            </span>
          </Link>
          
          <Button
            variant="ghost"
            size="icon"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            data-testid="mobile-menu-toggle"
            className="flex-shrink-0 w-12 h-12"
          >
            {mobileMenuOpen ? <X className="w-7 h-7" /> : <Menu className="w-7 h-7" />}
          </Button>
        </div>
        
        {/* Mobile Menu Dropdown */}
        {mobileMenuOpen && (
          <div className="absolute top-full left-0 right-0 bg-white border-b border-gray-200 shadow-lg animate-slide-up">
            <nav className="p-4 space-y-1">
              {allNavItems.map((item) => {
                const Icon = item.icon;
                const isActive = location.pathname === item.path || 
                               (item.path !== '/dashboard' && location.pathname.startsWith(item.path));
                
                return (
                  <Link
                    key={item.path}
                    to={item.path}
                    onClick={() => setMobileMenuOpen(false)}
                    className={cn(
                      "flex items-center gap-3 px-4 py-3 rounded-xl text-lg font-medium transition-all",
                      isActive 
                        ? "bg-primary text-white" 
                        : "text-muted-foreground hover:bg-accent"
                    )}
                    style={isActive ? { backgroundColor: primaryColor } : {}}
                  >
                    <Icon className="w-5 h-5" />
                    {item.label}
                  </Link>
                );
              })}
            </nav>
            <div className="p-4 border-t border-gray-100">
              <div className="flex items-center gap-3 mb-3">
                <div 
                  className="w-10 h-10 rounded-full flex items-center justify-center text-white font-medium"
                  style={{ backgroundColor: primaryColor }}
                >
                  {user?.name?.charAt(0) || 'U'}
                </div>
                <div>
                  <p className="font-medium">{user?.name}</p>
                  <p className="text-sm text-muted-foreground capitalize">{user?.role}</p>
                </div>
              </div>
              <Button 
                variant="ghost" 
                onClick={logout}
                className="w-full justify-start text-muted-foreground hover:text-destructive"
              >
                <LogOut className="w-4 h-4 mr-2" />
                {t('logout')}
              </Button>
            </div>
          </div>
        )}
      </header>
      
      {/* Main Content */}
      <main className="md:ml-56 lg:ml-64 pt-20 md:pt-0 pb-28 md:pb-8 min-h-screen">
        <div className="p-4 md:p-6 lg:p-8">
          <RouteErrorBoundary developerMode={developerMode}>
            <Outlet />
          </RouteErrorBoundary>
        </div>
      </main>
      
      {/* Mobile Bottom Navigation - Larger touch targets */}
      <nav className="md:hidden fixed bottom-0 left-0 right-0 bg-white border-t border-gray-200 px-2 z-50" style={{ paddingTop: '0.75rem', paddingBottom: 'max(0.75rem, env(safe-area-inset-bottom))' }}>
        <div className="flex justify-around items-center">
          <Link
            to="/dashboard"
            className={cn(
              "flex flex-col items-center gap-1.5 px-4 py-2 rounded-2xl min-w-[72px] transition-colors",
              location.pathname === '/dashboard' ? "text-primary bg-primary/10" : "text-muted-foreground"
            )}
            style={location.pathname === '/dashboard' ? { color: primaryColor } : {}}
            data-testid="mobile-nav-dashboard"
          >
            <LayoutDashboard className="w-7 h-7" />
            <span className="text-[11px] font-semibold">{t('dashboard')}</span>
          </Link>
          
          <Link
            to="/clients"
            className={cn(
              "flex flex-col items-center gap-1.5 px-4 py-2 rounded-2xl min-w-[72px] transition-colors",
              location.pathname.startsWith('/clients') ? "text-primary bg-primary/10" : "text-muted-foreground"
            )}
            style={location.pathname.startsWith('/clients') ? { color: primaryColor } : {}}
            data-testid="mobile-nav-clients"
          >
            <Users className="w-7 h-7" />
            <span className="text-[11px] font-semibold">{t(terminology)}</span>
          </Link>
          
          <button
            onClick={() => navigate('/site-visits?action=new')}
            className="flex flex-col items-center gap-1 px-5 py-3 rounded-2xl transition-transform active:scale-95 shadow-md -mt-4"
            style={{ backgroundColor: accentColor }}
            data-testid="mobile-nav-new-site-visit"
          >
            <Plus className="w-7 h-7 text-black" strokeWidth={2.5} />
            <span className="text-[11px] font-bold text-black">{t('create')}</span>
          </button>
          
          <Link
            to="/estimates"
            className={cn(
              "flex flex-col items-center gap-1.5 px-4 py-2 rounded-2xl min-w-[72px] transition-colors",
              location.pathname.startsWith('/estimates') && !location.pathname.includes('/new') 
                ? "text-primary bg-primary/10" 
                : "text-muted-foreground"
            )}
            style={location.pathname.startsWith('/estimates') && !location.pathname.includes('/new') ? { color: primaryColor } : {}}
            data-testid="mobile-nav-estimates"
          >
            <FileText className="w-7 h-7" />
            <span className="text-[11px] font-semibold">{t('estimates')}</span>
          </Link>
          
          <Link
            to="/invoices"
            className={cn(
              "flex flex-col items-center gap-1.5 px-4 py-2 rounded-2xl min-w-[72px] transition-colors",
              location.pathname.startsWith('/invoices') ? "text-primary bg-primary/10" : "text-muted-foreground"
            )}
            style={location.pathname.startsWith('/invoices') ? { color: primaryColor } : {}}
            data-testid="mobile-nav-invoices"
          >
            <Receipt className="w-7 h-7" />
            <span className="text-[11px] font-semibold">{t('invoices')}</span>
          </Link>
        </div>
      </nav>
    </div>
  );
};

export default AppLayout;
